// Probe de banda — diferente do tier (hardware), banda OSCILA.
// Mede a cada login/sessão, NÃO persiste em localStorage.
//
// Estratégia séria mas rápida:
// 1. Warmup (256KB descartado) → resolve TCP slow-start, evita medição inflada
// 2. Medição principal contra Cloudflare (anycast, sem chave, CORS aberto)
// 3. Timeout duro 5s — se estourar, usa o que mediu
// 4. Fallback no nosso /v1/probe/blob se Cloudflare falhar (ToS, firewall, etc.)
// 5. Detecção saveData/data-saver → reduz payload pra 2MB em mobile

export type BandwidthClass = 'poor' | 'low' | 'medium' | 'high' | 'ultra'

export interface BandwidthResult {
  /** Mbps medidos. null se medição falhou completamente. */
  mbps: number | null
  /** Classificação derivada — usar isso pra decisões. */
  class: BandwidthClass
  /** Quando foi medido (epoch ms). */
  measuredAt: number
  /** True se a medição falhou e estamos no fallback otimista. */
  failed: boolean
  /** Quem serviu o byte: 'cloudflare', 'self', 'none'. */
  source: 'cloudflare' | 'self' | 'none'
}

export interface ProbeProgress {
  /** Bytes baixados até agora (na fase de medição, não warmup). */
  bytesLoaded: number
  /** Tamanho total esperado (bytes). */
  bytesTotal: number
  /** Mbps instantâneo (média desde o início da fase de medição). */
  mbps: number
  /** Fase atual. */
  phase: 'warmup' | 'measuring' | 'done'
}

export interface MeasureOptions {
  /** Callback de progresso (1x por chunk recebido). */
  onProgress?: (p: ProbeProgress) => void
  /** AbortSignal externo — pra botão Skip do usuário. */
  signal?: AbortSignal
  /** Timeout total da medição em ms. Default 5000. */
  timeoutMs?: number
  /** URL do nosso fallback (se Cloudflare falhar). Default null = sem fallback. */
  fallbackUrl?: string | null
  /** Tamanho do payload em bytes. Override do default (10MB normal /
   *  2MB data-saver). Útil pra probes leves periódicos (ex: hook
   *  `useBandwidthRefresh` passa 1MB pra reduzir custo total). Aceito
   *  range 256KB-10MB; fora disso aplica clamp. */
  sizeBytes?: number
}

// Limiares Mbps → classe (conservador pra IPTV/HLS, que stalla cedo)
function classify(mbps: number): BandwidthClass {
  if (mbps < 2) return 'poor'
  if (mbps < 5) return 'low'
  if (mbps < 12) return 'medium'
  if (mbps < 30) return 'high'
  return 'ultra'
}

// Detecta data-saver (Chrome Android, alguns proxies de operadora)
function isDataSaver(): boolean {
  if (typeof navigator === 'undefined') return false
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  return !!conn?.saveData
}

// Tamanho do payload — caller pode overridear via `sizeBytes`. Default
// data-saver-aware. Clamp: 256KB ≤ size ≤ 10MB.
const PROBE_MIN_BYTES = 262_144 // 256KB
const PROBE_MAX_BYTES = 10_485_760 // 10MB
function payloadSize(override?: number): number {
  if (override !== undefined) {
    return Math.min(Math.max(override, PROBE_MIN_BYTES), PROBE_MAX_BYTES)
  }
  return isDataSaver() ? 2_097_152 : 10_485_760 // 2MB / 10MB
}

const CLOUDFLARE_DOWN = 'https://speed.cloudflare.com/__down'
const WARMUP_BYTES = 262_144 // 256KB pro TCP sair do slow-start

let cached: BandwidthResult | null = null

/**
 * Baixa via fetch streaming, reportando progresso. Lança em erro/abort.
 */
async function downloadAndMeasure(
  url: string,
  expectedBytes: number,
  opts: MeasureOptions,
  startMs: number,
): Promise<{ mbps: number; bytesLoaded: number }> {
  // Sem header customizado (`x-probe` quebrava CORS preflight no
   // Cloudflare CDN — `Request header field x-probe is not allowed
  // by Access-Control-Allow-Headers`). Identificação no nosso backend
  // pode ser via query string (`url?_probe=1`) se necessário.
  // Bug Fernando 2026-05-09.
  const res = await fetch(url, {
    cache: 'no-store',
    signal: opts.signal ?? null,
  })
  if (!res.ok) throw new Error(`probe http ${res.status}`)

  let bytesLoaded = 0
  // Streaming via reader → reporta progresso. Fallback pra arrayBuffer em
  // browsers/TVs antigas que não suportam Response.body.
  if (res.body && typeof res.body.getReader === 'function') {
    const reader = res.body.getReader()
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      if (value) {
        bytesLoaded += value.byteLength
        const elapsedSec = (performance.now() - startMs) / 1000
        const mbps = elapsedSec > 0 ? (bytesLoaded * 8) / 1_000_000 / elapsedSec : 0
        opts.onProgress?.({
          bytesLoaded,
          bytesTotal: expectedBytes,
          mbps,
          phase: 'measuring',
        })
      }
    }
  } else {
    const buf = await res.arrayBuffer()
    bytesLoaded = buf.byteLength
  }

  const elapsedSec = (performance.now() - startMs) / 1000
  if (elapsedSec <= 0 || bytesLoaded === 0) throw new Error('probe elapsed/size 0')
  const mbps = (bytesLoaded * 8) / 1_000_000 / elapsedSec
  return { mbps, bytesLoaded }
}

/**
 * Medição completa: warmup + medição. Tenta Cloudflare; se falhar, fallback.
 */
export async function measureBandwidth(opts: MeasureOptions = {}): Promise<BandwidthResult> {
  const timeoutMs = opts.timeoutMs ?? 5000
  const size = payloadSize(opts.sizeBytes)

  // Combina signals (timeout + opcional do usuário)
  const ctl = new AbortController()
  const onAbort = () => ctl.abort()
  if (opts.signal) {
    if (opts.signal.aborted) ctl.abort()
    else opts.signal.addEventListener('abort', onAbort, { once: true })
  }
  const timeoutId = setTimeout(() => ctl.abort(), timeoutMs)
  const innerOpts: MeasureOptions = { ...opts, signal: ctl.signal }

  try {
    // === Warmup (descartado) ===
    opts.onProgress?.({ bytesLoaded: 0, bytesTotal: size, mbps: 0, phase: 'warmup' })
    try {
      await fetch(`${CLOUDFLARE_DOWN}?bytes=${WARMUP_BYTES}&_=${Date.now()}`, {
        cache: 'no-store',
        signal: ctl.signal,
      }).then((r) => r.arrayBuffer())
    } catch {
      // warmup pode falhar silenciosamente — continuamos
    }

    // === Medição Cloudflare ===
    try {
      const start = performance.now()
      const { mbps } = await downloadAndMeasure(
        `${CLOUDFLARE_DOWN}?bytes=${size}&_=${Date.now()}`,
        size,
        innerOpts,
        start,
      )
      const result: BandwidthResult = {
        mbps,
        class: classify(mbps),
        measuredAt: Date.now(),
        failed: false,
        source: 'cloudflare',
      }
      cached = result
      opts.onProgress?.({ bytesLoaded: size, bytesTotal: size, mbps, phase: 'done' })
      return result
    } catch (cfErr) {
      // Aborto do usuário (skip) ou timeout: NÃO tenta fallback
      if (ctl.signal.aborted) throw cfErr
      // Cloudflare falhou (CORS, bloqueio, rede) — tenta fallback se houver
      if (!opts.fallbackUrl) throw cfErr
    }

    // === Fallback no próprio backend ===
    const fbStart = performance.now()
    const fbSize = Math.min(size, 5_242_880) // backend limita a 5MB
    const { mbps } = await downloadAndMeasure(
      `${opts.fallbackUrl}?size=${fbSize}&_=${Date.now()}`,
      fbSize,
      innerOpts,
      fbStart,
    )
    const result: BandwidthResult = {
      mbps,
      class: classify(mbps),
      measuredAt: Date.now(),
      failed: false,
      source: 'self',
    }
    cached = result
    opts.onProgress?.({ bytesLoaded: fbSize, bytesTotal: fbSize, mbps, phase: 'done' })
    return result
  } catch {
    // Tudo falhou (rede off, abort, timeout sem nada baixado)
    const result: BandwidthResult = {
      mbps: null,
      class: 'medium', // fallback otimista mas não absurdo
      measuredAt: Date.now(),
      failed: true,
      source: 'none',
    }
    cached = result
    opts.onProgress?.({ bytesLoaded: 0, bytesTotal: size, mbps: 0, phase: 'done' })
    return result
  } finally {
    clearTimeout(timeoutId)
    if (opts.signal) opts.signal.removeEventListener('abort', onAbort)
  }
}

/** Última medição da sessão. Null se ainda não mediu. */
export function getCachedBandwidth(): BandwidthResult | null {
  return cached
}

/** Limpa cache — útil em logout pra forçar nova medição no próximo login. */
export function clearBandwidthCache(): void {
  cached = null
}
